// Markdown renderer for assistant chat messages.
//
// Renders markdown (headers, lists, tables, code blocks, bold/italic, links)
// using react-markdown + remark-gfm (tables/strikethrough) + rehype-sanitize
// (strip any raw HTML / dangerous nodes). react-markdown v10 does not render
// raw HTML by default; rehype-sanitize is an extra defense-in-depth layer so
// model output can never inject HTML/scripts into the chat.
//
// Styling uses the existing Tailwind slate palette so assistant messages look
// consistent with the rest of the app. USER messages are NOT rendered here --
// they stay plain text (`whitespace-pre-wrap`) in ChatPanel.

import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { Components } from 'react-markdown';

const components: Components = {
  h1: ({ children }) => (
    <h1 className="text-base font-bold text-slate-900 mt-3 mb-1.5">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-bold text-slate-900 mt-3 mb-1.5">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-slate-800 mt-2.5 mb-1">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-slate-800 mt-2 mb-1">{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 className="text-[13px] font-semibold text-slate-800 mt-2 mb-0.5">{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 className="text-[13px] font-semibold text-slate-700 mt-2 mb-0.5">{children}</h6>
  ),
  p: ({ children }) => <p className="my-1.5 leading-relaxed">{children}</p>,
  ul: ({ children }) => (
    <ul className="list-disc pl-5 my-1.5 space-y-0.5">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 my-1.5 space-y-0.5">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-indigo-600 underline hover:text-indigo-700"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-900">{children}</strong>
  ),
  em: ({ children }) => <em className="italic text-slate-700">{children}</em>,
  del: ({ children }) => <del className="line-through text-slate-500">{children}</del>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-slate-300 pl-3 my-2 text-slate-600 italic">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-slate-200" />,
  // Inline code: slate-tinted pill. Fenced code blocks arrive as
  // <pre><code>; react-markdown v10 gives the block <code> a `language-xxx`
  // className when a fence language is set, but a fence without a language
  // has no className. We detect block vs inline by className OR multiline
  // content (block code spans lines; inline code does not) so block code is
  // rendered plain and the <pre> wrapper supplies the dark styling.
  code: ({ children, className }) => {
    const isBlock =
      /language-/.test(className || '') ||
      String(children).includes('\n');
    if (isBlock) {
      return (
        <code className={`${className ?? ''} font-mono text-slate-100`}>
          {children}
        </code>
      );
    }
    return (
      <code className="px-1 py-0.5 rounded bg-slate-200 text-slate-800 text-[12px] font-mono">
        {children}
      </code>
    );
  },
  // Fenced code block: slate-900 background + monospace + horizontal scroll.
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto rounded-md bg-slate-900 p-3 text-[12px] leading-relaxed">
      {children}
    </pre>
  ),
  // Tables: bordered, striped header, slate palette.
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full border-collapse text-[13px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-200">{children}</thead>,
  th: ({ children }) => (
    <th className="border border-slate-300 px-2.5 py-1 text-left font-semibold text-slate-800">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-slate-300 px-2.5 py-1 text-slate-700">{children}</td>
  ),
};

interface MarkdownRendererProps {
  content: string;
}

export default function MarkdownRenderer({ content }: MarkdownRendererProps) {
  return (
    <div className="markdown-body text-sm text-slate-800 break-words">
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={components}
      >
        {content}
      </Markdown>
    </div>
  );
}